import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Link2, Receipt, CalendarClock, Droplets, Scissors, Palette, Wind } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

// Three different clients each sequence: distinct weight signature + price set (AU $).
const SIGNATURES = [72.4, 88.1, 61.7];
const PRICESETS = [
  { cut: 45, colour: 110, style: 45 },
  { cut: 55, colour: 140, style: 60 },
  { cut: 32, colour: 95, style: 40 },
];
const CHARGEABLE = [1, 2, 3];
// Each client is stitched across several numbered chairs — wash basin included — so the
// weight signature can be followed station to station.
const CLIENTS = [
  { name: "Sarah", services: "Cut + Blow-dry", stylist: "Alex", primary: "Chair 2",
    chairs: { wash: "Chair 6", cut: "Chair 2", colour: "Chair 2", style: "Chair 2" } },
  { name: "Mia", services: "Cut + Colour + Style", stylist: "Alex", primary: "Chair 3",
    chairs: { wash: "Chair 6", cut: "Chair 3", colour: "Chair 4", style: "Chair 3" } },
  { name: "Jo", services: "Full Colour + Cut", stylist: "Alex", primary: "Chair 5",
    chairs: { wash: "Chair 7", cut: "Chair 5", colour: "Chair 4", style: "Chair 5" } },
];
const ORDER = ["matched", "leak", "noshow"];

// Timeline: 0 booking · 1..4 visit · 5,6 cash-up (two beats) · 7 result
export default function StitchingDemo({ onLeak }) {
  const { money } = useBranding();
  const [step, setStep] = useState(0);
  const [scenario, setScenario] = useState("matched");
  const [seqIdx, setSeqIdx] = useState(1);
  const [missingIdx, setMissingIdx] = useState(2);

  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => {
        if (s >= 7) {
          setScenario((sc) => {
            const next = ORDER[(ORDER.indexOf(sc) + 1) % ORDER.length];
            if (next === "leak") setMissingIdx(CHARGEABLE[Math.floor(Math.random() * CHARGEABLE.length)]);
            return next;
          });
          setSeqIdx((n) => (n + 1) % PRICESETS.length);
          return 0;
        }
        return s + 1;
      });
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const prices = PRICESETS[seqIdx];
  const weight = SIGNATURES[seqIdx];
  const client = CLIENTS[seqIdx];
  const STATIONS = [
    { key: "wash", label: "Wash", note: "no charge", price: 0, Icon: Droplets, chair: client.chairs.wash },
    { key: "cut", label: "Cut", note: "service", price: prices.cut, Icon: Scissors, chair: client.chairs.cut },
    { key: "colour", label: "Colour", note: "service", price: prices.colour, Icon: Palette, chair: client.chairs.colour },
    { key: "style", label: "Style", note: "service", price: prices.style, Icon: Wind, chair: client.chairs.style },
  ];
  const TICKET_TOTAL = STATIONS.reduce((n, s) => n + s.price, 0);

  const phase = step === 0 ? "booking" : step <= 4 ? "visit" : step <= 6 ? "cashup" : "result";
  const leak = scenario === "leak";
  const noshow = scenario === "noshow";
  const activeStation = step - 1;
  const leakAmt = STATIONS[missingIdx].price;

  useEffect(() => {
    if (step === 7 && scenario === "leak" && onLeak) {
      onLeak({ chair_label: `${STATIONS[missingIdx].chair} · ${STATIONS[missingIdx].label}`, expected_price: leakAmt, confidence: 98, duration_min: 22, classification: STATIONS[missingIdx].label.toLowerCase() });
    }
  }, [step, scenario]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingTag =
    phase === "booking" ? { label: "awaiting arrival", cls: "bg-slate-500/20 text-slate-300" }
      : noshow ? { label: "no-show", cls: "bg-amber-500/20 text-amber-300" }
        : phase === "result"
          ? (leak ? { label: `short ${money(leakAmt)}`, cls: "bg-red-500/20 text-red-300" }
                  : { label: "reconciled ✓", cls: "bg-emerald-500/20 text-emerald-300" })
          : { label: "auto-linked to seated client", cls: "bg-sky-500/20 text-sky-300" };

  return (
    <div className="surface-card p-5" data-testid="stitching-demo">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-[#38BDF8]" />
        <h3 className="font-display text-base font-medium">Pre-booked visit — booking · chairs · one ticket</h3>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        A three-way check: the <strong className="text-slate-200">booking</strong> arms an expected service, the <strong className="text-slate-200">weight signature</strong> proves it happened, and the <strong className="text-slate-200">POS</strong> proves it was rung up.
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-[#2A3550] bg-[#171E2E] px-3 py-2" data-testid="stitch-booking">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarClock className="h-4 w-4 text-[#38BDF8] shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-100 truncate">{`2:30 PM · ${client.services} · ${client.primary} · with ${client.stylist}`}</div>
            <div className="text-[10px] text-slate-500">Pre-booked online · {money(TICKET_TOTAL)} expected · signature {weight} kg</div>
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${bookingTag.cls}`}>{bookingTag.label}</span>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3">
        {STATIONS.map((st, i) => {
          const isMissing = phase === "result" && leak && i === missingIdx;
          let state;
          if (noshow) state = "idle";
          else if (phase === "result") state = isMissing ? "missing" : "rung";
          else if (phase === "visit") state = i === activeStation ? "active" : i < activeStation ? "seated" : "idle";
          else state = phase === "cashup" ? "seated" : "idle";
          const cls = {
            active: "border-[#38BDF8] bg-sky-500/15",
            seated: "border-sky-500/30 bg-sky-500/5",
            rung: "border-emerald-500/50 bg-emerald-500/10",
            missing: "border-red-500/60 bg-red-500/10",
            idle: "border-[#2A3550] bg-[#1E2638]",
          }[state];
          const iconCls = { active: "text-[#38BDF8]", seated: "text-slate-300", rung: "text-emerald-400", missing: "text-red-400", idle: "text-slate-400" }[state];
          let note;
          if (phase === "result") note = noshow ? (st.price ? "not delivered" : "—") : isMissing ? "not rung up" : st.key === "wash" ? "no charge" : "on ticket";
          else note = st.price ? money(st.price) : st.note;
          const Icon = st.Icon;
          return (
            <div key={st.key} data-testid={`stitch-station-${st.key}`}
              className={`relative rounded-xl border p-3 text-center transition-colors duration-300 ${cls}`}>
              <Icon className={`h-5 w-5 mx-auto ${iconCls}`} />
              <div className="mt-1 text-xs font-medium text-slate-100">{st.label}</div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500" data-testid={`stitch-chair-${st.key}`}>{st.chair}</div>
              <div className={`text-[10px] ${isMissing ? "text-red-300" : "text-slate-500"}`}>{note}</div>
              {state === "rung" && <Check className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-emerald-400" />}
              {state === "missing" && <X className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-red-400" />}
              {state === "active" && (
                <motion.div layoutId="stitch-sig"
                  className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#38BDF8] px-2 py-0.5 text-[10px] font-semibold text-[#0B1120] shadow-lg">
                  {weight} kg
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      <div className={`mt-6 flex items-start gap-2 rounded-lg border px-4 py-3 text-sm transition-colors duration-300 ${
        phase !== "result" ? "border-[#2A3550] bg-[#171E2E] text-slate-400"
          : noshow ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
          : leak ? "border-red-500/50 bg-red-500/10 text-red-300"
          : "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
      }`} data-testid="stitch-result">
        {phase === "result" && <Receipt className="h-4 w-4 mt-0.5 shrink-0" />}
        <span>
          {phase === "booking"
            ? `Booking received — expected ${money(TICKET_TOTAL)}. Armed and waiting for the client to sit…`
            : phase === "visit"
              ? (noshow ? `2:30 booking window open — ${client.primary} still empty, no weight signature detected…` : `${client.name} arrived (${weight} kg) — stitched from ${STATIONS[0].chair} across to ${client.primary} as the signature moves station to station…`)
              : phase === "cashup"
                ? (noshow ? "No client, and no sale at the POS…" : "Visit complete — cashing up at the POS…")
                : noshow
                  ? `○ No-show — the ${money(TICKET_TOTAL)} booking was never occupied and never charged. Flagged as a no-show, not leakage (nothing was delivered).`
                  : leak
                    ? `⚠ ${STATIONS[missingIdx].label} was booked and delivered but never rung at the POS — ${money(leakAmt)} leaked against the ${money(TICKET_TOTAL)} booking · 98% confidence (ticket rang only ${money(TICKET_TOTAL - leakAmt)}).`
                    : `✓ Booking reconciled — every service rung on ticket #POS-4F2A · ${money(TICKET_TOTAL)} matches the ${money(TICKET_TOTAL)} booked.`}
        </span>
      </div>
    </div>
  );
}
